import type { CreateEnquirySessionPayload } from 'api/accommodationBooking'
import { buildSessionCreatedContext, buildSessionLoyaltyUpgrade, getHotelOnlyPropertyRatePricing, getHotelPricing } from 'lib/accommodation/buildEnquirySessionPayload'
import { getAccommodationV2RateIdentity, mapPropertyRateRef } from 'lib/checkout/accommodation/cart'

function mapSupplierMetaData(
  rate: App.RawPropertyRate,
): CreateEnquirySessionPayload['supplierMetaData'] {
  if (rate.service.type === 'bedbank') {
    return {
      internalService: 'bedbank',
      selectedRate: {
        optionId: rate.optionId,
        internalRateId: rate.service.rateId,
        ...(rate.service.rateGroupId ? { rateGroupId: rate.service.rateGroupId } : {}),
        ...(rate.service.bedGroups[0]?.id ? { bedGroupId: rate.service.bedGroups[0].id } : {}),
      },
      internalPropertyId: rate.service.propertyId,
      internalRoomId: rate.service.roomId,
    }
  }

  return {
    internalService: 'reservation',
    selectedRate: {
      optionId: rate.optionId,
      internalRateId: rate.service.rateId,
      ...(rate.service.ratePlanId ? { ratePlanId: rate.service.ratePlanId } : {}),
      ...(rate.groupKey ? { rateGroupId: rate.groupKey } : {}),
    },
    internalPropertyId: rate.service.propertyId,
    internalRoomId: rate.service.roomId,
  }
}

/**
 * An applied upgrade's discount is the target's price above the source's. The target is the rate
 * being re-sessioned, so its pricing follows the live rate; the benefit payment does the same.
 */
function withLiveTargetPricing(
  upgrade: App.Checkout.PropertyRateLoyaltyUpgrade | undefined,
  rate: App.RawPropertyRate,
): App.Checkout.PropertyRateLoyaltyUpgrade | undefined {
  if (!upgrade || upgrade.applied === false) return upgrade
  return { ...upgrade, targetPricing: getHotelOnlyPropertyRatePricing(rate) }
}

/**
 * Points a cart item at its replacement session. Every field that add-to-cart derives from the rate
 * is refreshed from the live rate, so the item's snapshot matches the session it now names. The
 * pricing comes from `getHotelPricing`, as in the replacement session payload.
 *
 * `rate` is a raw checkout-enquiry rate, not an enriched one:
 * - it has no availability session, so the shopped `accommAvailabilitySessionId` is kept;
 * - its totals carry no flight enrichment, so no flight overhead is taken off the sell total.
 *
 * Occupancy, dates, property, room and bundles stay as they are, as on the replacement session.
 * Loyalty upgrade metadata also stays, with an applied upgrade's target pricing refreshed.
 */
export function applyAccommodationV2ReplacementSession(
  item: App.Checkout.AccommodationV2Item,
  rate: App.RawPropertyRate,
  sessionId: string,
): App.Checkout.AccommodationV2Item {
  const pricing = getHotelPricing(rate)

  return {
    ...item,
    // A restored cart brings back the superseded rate's svc-cart totals. Clear them: the svc-cart
    // save recomputes them from the item view, which prices from the new session.
    total: undefined,
    memberTotal: undefined,
    nonMemberTotal: undefined,
    sessionId,
    ...getAccommodationV2RateIdentity(rate),
    optionId: rate.optionId,
    sellTotal: pricing.sellTotal,
    currency: pricing.currency,
    // Assigned even when absent, so the superseded rate's values are cleared rather than kept.
    propertyFees: pricing.propertyFees,
    luxPlusTotal: pricing.luxPlusTotal,
    paymentMethod: rate.paymentMethod.type,
    token: rate.token,
    isFlightBundle: rate.isFlightBundle,
    boardCode: rate.boardCode,
    accommCampaignId: rate.promotion?.isCampaign ? rate.promotion.campaignId : undefined,
    ...(item.propertyRateRef && {
      propertyRateRef: mapPropertyRateRef(rate, item.propertyRateRef.bundleId),
    }),
    loyaltyPropertyRateUpgrade: withLiveTargetPricing(item.loyaltyPropertyRateUpgrade, rate),
  }
}

/**
 * Rebuilds a session-create payload after a `price_changed` enquiry.
 * Pricing and rate identity come from the live rate; occupancy, dates, property,
 * room, and bundles stay on the existing session. LuxPlus+ eligibility and LPP member pricing
 * come from the caller, as for `buildEnquirySessionPayloadFromCartItem`.
 *
 * The price token also comes from the live rate, and is omitted when that rate has none.
 * Never fall back to the existing session's token: the price change invalidated it.
 */
export function buildReplacementEnquirySessionPayload(
  session: App.AccommodationEnquirySession,
  rate: App.RawPropertyRate,
  isLuxPlusUser: boolean,
  lppMemberPricingEnabled: boolean,
  /** The item's loyalty package upgrade, so the replacement keeps carrying the discount. */
  loyaltyUpgrade?: App.Checkout.PropertyRateLoyaltyUpgrade,
): CreateEnquirySessionPayload {
  const { occupancy, property, room, checkIn, checkOut, region, bundle, optInBundles } = session.data

  return {
    pricing: getHotelPricing(rate),
    property: { propertyId: property.propertyId },
    room: { roomId: room.roomId },
    occupancy,
    supplierMetaData: mapSupplierMetaData(rate),
    checkIn,
    checkOut,
    region,
    currency: rate.currency,
    ...(bundle ? { bundle } : {}),
    ...(optInBundles?.length ? { optInBundles } : {}),
    ...buildSessionLoyaltyUpgrade(withLiveTargetPricing(loyaltyUpgrade, rate), isLuxPlusUser, lppMemberPricingEnabled),
    ...buildSessionCreatedContext(isLuxPlusUser, lppMemberPricingEnabled),
  }
}
